import { PushNotification } from "@repo/helpers";
import { FilterItem, KeyedArray } from "@repo/models";
import { IncidentHistory } from "@repo/models";
import { useCallback } from "react";

import { SurveyResponse } from "../models/surveys";
import * as Queries from "./queries";
import { useFilterItemV2QueryWrapper, useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useRepIncidentApi() {
	const { Get, Post } = useRepPortalApi();
	const GridFiltersApi = useRepPortalApi("/MVC/GridFilters");

	const getHistory = useCallback(
		function (incidentId: number) {
			return Get<IncidentHistory[]>("IncidentHistories", { incidentId });
		},
		[Get],
	);

	const loadHistories = useCallback(
		function (incidentId: number) {
			return async function () {
				if (!incidentId) return defaultArr();
				try {
					const histories = await getHistory(incidentId);
					const keyedItems = new KeyedArray<IncidentHistory>(histories, (h) => h.id);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve background checks");
					return defaultArr();
				}
			};
		},
		[getHistory],
	);

	const getSurveyResponses = useCallback(
		function (complaintId: number) {
			return Get<SurveyResponse[]>("ComplaintSurveyResponse", { complaintId });
		},
		[Get],
	);

	const loadSurveyResponses = useCallback(
		function (incidentId: number) {
			return async function () {
				if (!incidentId) return defaultSurveyResponseArr();
				try {
					const responses = await getSurveyResponses(incidentId);
					const keyedItems = new KeyedArray<SurveyResponse>(responses, (h) => h.surveyResponseId);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve survey responses");
					return defaultSurveyResponseArr();
				}
			};
		},
		[getSurveyResponses],
	);

	const getComplaintActionsTaken = useCallback(
		function () {
			return Get<FilterItem[]>("GetComplaintActionsTakenDyn");
		},
		[Get],
	);

	const getUsers = useCallback(
		function () {
			return GridFiltersApi.Get<FilterItem[]>("GetUsersDyn");
		},
		[GridFiltersApi.Get],
	);

	const assignToQaAnalyst = useCallback(
		function (entityType: string) {
			return async (incidentId: number, userId: number) =>
				Post<string>("AssignToAnalyst", {
					entityType,
					incidentId,
					userId,
				});
		},
		[Post],
	);

	return {
		loadHistories,
		loadSurveyResponses,
		getComplaintActionsTaken,
		getUsers,
		assignToQaAnalyst,
	};
}

export function useRepIncidentQueries() {
	const { loadHistories, loadSurveyResponses, getComplaintActionsTaken, getUsers } = useRepIncidentApi();

	const useHistoriesQuery = (incidentId: number) =>
		useQueryWrapper(Queries.REP_INCIDENT.HISTORY, [incidentId, loadHistories], loadHistories(incidentId));

	const useSurveyResponsesQuery = (incidentId: number) =>
		useQueryWrapper(
			Queries.REP_INCIDENT.SURVEY_RESPONSES,
			[incidentId, loadSurveyResponses],
			loadSurveyResponses(incidentId),
		);

	const useComplaintActionsQuery = () =>
		useFilterItemV2QueryWrapper(Queries.COMPLAINT.ACTIONS, [getComplaintActionsTaken], () =>
			getComplaintActionsTaken(),
		);

	const useUsersQuery = () => useFilterItemV2QueryWrapper(Queries.COMPLAINT.USERS, [loadHistories], getUsers);

	return {
		useHistoriesQuery,
		useSurveyResponsesQuery,
		useComplaintActionsQuery,
		useUsersQuery,
	};
}
const defaultArr = () => new KeyedArray<IncidentHistory>([], (h) => h.id);
const defaultSurveyResponseArr = () => new KeyedArray<SurveyResponse>([], (h) => h.surveyResponseId);
